import React, { ReactNode, useState } from 'react';
import styled from 'styled-components';
import {
  flip,
  safePolygon,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { ISignificanceVariantStats } from '../../interfaces';
import { MetricTypeKey } from '../../../../utils/apollo/graphqlSchema';
import * as SignificanceTableUI from './SignificanceTableUI';
import InformationalTooltip from '../../../InformationalTooltip';
import {
  significanceChiSquareInfo,
  significanceDeltaInfo,
  significanceLiftConfidenceIntervalInfo,
  significanceLiftInfo,
  significanceMeanInfo,
  significancePowerInfo,
  significanceSampleSizeInfo,
  significanceStatSigInfo,
  significanceStdDeviationInfo,
  significanceWelchTestInfo,
} from '../tooltips';
import { significanceStErrorInfo } from '../../../../pages/Formulas/Significance/SignificanceBuilder/utils/tooltips';
import {
  defaultNumberFormatOptions,
  globalFormat,
} from '../../../../utils/formatting';
import { floatingComponentStyles } from '../../../../styles/shareableStyles';
import { theme } from '../../../../styles/theme';
import { FloatingLayer } from '../../../FloatingManager';

interface IProps {
  children: ReactNode;
  variant: ISignificanceVariantStats;
  metricTypeKey: MetricTypeKey;
  label: string;
}

const Container = styled.div`
  ${floatingComponentStyles};
  padding: 10px 12px;
  font-size: 12px;
  line-height: normal;
  color: ${theme.colors.secondary800};
`;

const VariantName = styled.div`
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.secondary900};
`;
const Heading = styled.div`
  display: inline-flex;
  align-items: center;
  column-gap: 4px;
  padding: 8px;
  font-weight: 600;
  color: ${theme.colors.secondary900};
  white-space: nowrap;
`;
const Value = styled.td`
  padding: 8px;
  white-space: nowrap;
`;

const SignificanceVariantStatsPopover = ({
  children,
  variant,
  metricTypeKey,
  label,
}: IProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [flip()],
    placement: 'bottom-start',
    strategy: 'fixed',
  });

  const hover = useHover(context, {
    handleClose: safePolygon(),
  });
  const dismiss = useDismiss(context, {
    ancestorScroll: true,
  });
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    dismiss,
    role,
  ]);

  return (
    <>
      <SignificanceTableUI.BodyCell
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {children}
      </SignificanceTableUI.BodyCell>
      {isOpen && (
        <FloatingLayer>
          <Container
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <VariantName>{label}</VariantName>
            <SignificanceTableUI.Table>
              <tbody>
                <tr>
                  <td>
                    <Heading>
                      Lift
                      <InformationalTooltip
                        tooltipOption={significanceLiftInfo}
                      />
                    </Heading>
                  </td>
                  <Value>
                    {globalFormat.formatNumber(
                      variant.lift,
                      defaultNumberFormatOptions.percent,
                    )}
                  </Value>
                </tr>
                <tr>
                  <td>
                    <Heading>
                      Lift Confidence Interval
                      <InformationalTooltip
                        tooltipOption={significanceLiftConfidenceIntervalInfo}
                      />
                    </Heading>
                  </td>
                  <Value>
                    {globalFormat.formatNumber(
                      variant.confidenceInterval.lowerBound,
                      defaultNumberFormatOptions.percent,
                    )}
                    ,
                    {globalFormat.formatNumber(
                      variant.confidenceInterval.upperBound,
                      defaultNumberFormatOptions.percent,
                    )}
                  </Value>
                </tr>
                <tr>
                  <td>
                    <Heading>
                      Stat Sig
                      <InformationalTooltip
                        tooltipOption={significanceStatSigInfo}
                      />
                    </Heading>
                  </td>
                  <Value>
                    {variant.statisticalSignificance ? 'True' : 'False'}{' '}
                    (P-Value ={' '}
                    {globalFormat.formatNumber(
                      variant.pValue,
                      defaultNumberFormatOptions.scientific,
                    )}
                    )
                  </Value>
                </tr>
                <tr>
                  <td>
                    <Heading>
                      Power (# of experiments)
                      <InformationalTooltip
                        tooltipOption={significancePowerInfo}
                      />
                    </Heading>
                  </td>
                  <Value>
                    {globalFormat.formatNumber(
                      variant.statisticalPower,
                      defaultNumberFormatOptions.originDecimalPoint,
                    )}{' '}
                    (
                    {globalFormat.formatNumber(
                      variant.executedSimulations,
                      defaultNumberFormatOptions.originDecimalPoint,
                    )}
                    )
                  </Value>
                </tr>
                <tr>
                  <td>
                    <Heading>
                      Test Score
                      <InformationalTooltip
                        tooltipOption={
                          metricTypeKey === MetricTypeKey.Funnel
                            ? significanceChiSquareInfo
                            : significanceWelchTestInfo
                        }
                      />
                    </Heading>
                  </td>
                  <Value>
                    {globalFormat.formatNumber(
                      variant.testScore,
                      defaultNumberFormatOptions.originDecimalPoint,
                    )}
                  </Value>
                </tr>
                <tr>
                  <td>
                    <Heading>
                      Delta
                      <InformationalTooltip
                        tooltipOption={significanceDeltaInfo}
                      />
                    </Heading>
                  </td>
                  <Value>
                    {globalFormat.formatNumber(
                      variant.delta,
                      defaultNumberFormatOptions.originDecimalPoint,
                    )}
                  </Value>
                </tr>
                <tr>
                  <td>
                    <Heading>
                      Mean
                      <InformationalTooltip
                        tooltipOption={significanceMeanInfo}
                      />
                    </Heading>
                  </td>
                  <Value>
                    {globalFormat.formatNumber(
                      variant.mean,
                      defaultNumberFormatOptions.originDecimalPoint,
                    )}
                  </Value>
                </tr>
                <tr>
                  <td>
                    <Heading>
                      Std Deviation
                      <InformationalTooltip
                        tooltipOption={significanceStdDeviationInfo}
                      />
                    </Heading>
                  </td>
                  <Value>
                    {globalFormat.formatNumber(
                      variant.standardDeviation,
                      defaultNumberFormatOptions.originDecimalPoint,
                    )}
                  </Value>
                </tr>
                <tr>
                  <td>
                    <Heading>
                      Sample Size
                      <InformationalTooltip
                        tooltipOption={significanceSampleSizeInfo}
                      />
                    </Heading>
                  </td>
                  <Value>
                    {globalFormat.formatNumber(
                      variant.sampleSize,
                      defaultNumberFormatOptions.originDecimalPoint,
                    )}
                  </Value>
                </tr>
                {metricTypeKey !== MetricTypeKey.Funnel && (
                  <tr>
                    <td>
                      <Heading>
                        Std Error
                        <InformationalTooltip
                          tooltipOption={significanceStErrorInfo}
                        />
                      </Heading>
                    </td>
                    <Value>
                      {globalFormat.formatNumber(
                        variant.standardError,
                        defaultNumberFormatOptions.originDecimalPoint,
                      )}
                    </Value>
                  </tr>
                )}
              </tbody>
            </SignificanceTableUI.Table>
          </Container>
        </FloatingLayer>
      )}
    </>
  );
};

export default SignificanceVariantStatsPopover;
